import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, MapPin, Loader2 } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PayPalTrust } from "@/components/PayPalTrust";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

export default function Book() {
  const [params] = useSearchParams();
  const sessionId = params.get("session");
  const { customer } = useCustomerAuth();
  const [sessions, setSessions] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // booking object
  const [payLoading, setPayLoading] = useState(false);
  const [packBalance, setPackBalance] = useState(0);
  const [usePack, setUsePack] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", pet_name: "", pet_species: "Dog", date: "", time: "", notes: "" });

  useEffect(() => {
    api.get("/public/schedule").then((r) => setSessions(r.data)).catch(() => {});
    api.get("/public/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (sessionId && sessions.length) {
      const s = sessions.find((x) => x.id === sessionId);
      if (s) { setSelectedSession(s); setForm((f) => ({ ...f, date: s.date, time: s.time, notes: `Park session: ${s.park_name}` })); }
    }
  }, [sessionId, sessions]);

  useEffect(() => {
    if (form.email && form.email.includes("@")) {
      api.get(`/public/bundle-balance?email=${encodeURIComponent(form.email)}`)
        .then((r) => setPackBalance(r.data.remaining)).catch(() => setPackBalance(0));
    } else {
      setPackBalance(0);
    }
  }, [form.email]);

  useEffect(() => {
    if (customer) {
      setForm((f) => ({ ...f, name: f.name || customer.name || "", email: f.email || customer.email || "" }));
    }
  }, [customer]);

  useEffect(() => {
    // Pack autofill: auto-apply a signed-in customer's remaining credits so they book in one tap.
    if (customer && packBalance > 0) setUsePack(true);
  }, [customer, packBalance]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, service_id: services[0]?.id || "", session_id: sessionId || "", use_pack: usePack && packBalance > 0 };
      const { data } = await api.post("/public/bookings", payload);
      setDone(data);
      toast.success("Booking request sent! Check your email for confirmation.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const payNow = async () => {
    setPayLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { kind: "booking", ref_id: done.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPayLoading(false);
    }
  };

  return (
    <div className="bg-white text-brand-ink min-h-screen">
      <PublicNav />
      <section className="pt-32 pb-24 px-6 lg:px-10 max-w-3xl mx-auto">
        {done ? (
          <div className="text-center rounded-[2rem] border border-brand-line p-12" data-testid="booking-success">
            <CheckCircle2 className="w-14 h-14 text-brand-red mx-auto" />
            <h1 className="font-display text-4xl mt-6">Request received!</h1>
            <p className="mt-4 font-serif text-xl text-brand-ink/70">
              Thanks {done.client_name.split(" ")[0]} — we've emailed your confirmation. We'll reach out to finalize your 45-minute session.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              {done.deposit_paid ? (
                <span className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-green-600 text-white font-medium" data-testid="booking-pack-covered">Covered by your session pack ✓</span>
              ) : (
                <button onClick={payNow} disabled={payLoading} className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors disabled:opacity-60" data-testid="booking-pay-btn">
                  {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Pay $65 online now
                </button>
              )}
              <Link to="/schedule" className="px-8 py-4 rounded-full border border-brand-line font-medium hover:border-brand-red hover:text-brand-red transition-colors">Back to schedule</Link>
            </div>
            {!done.deposit_paid && <p className="mt-4 text-xs text-brand-ink/40">Prefer $60? Reserve from the app instead.</p>}
            {!done.deposit_paid && <PayPalTrust className="mt-5" />}
          </div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-[0.35em] text-brand-red font-semibold mb-5">Reserve Your Spot</div>
            <h1 className="font-display text-5xl">Book a Session</h1>
            <p className="mt-5 font-serif text-xl text-brand-ink/70">45-minute myofascial yoga for you and your pet. $60 in-app · $65 online.</p>
            {selectedSession && (
              <div className="mt-6 flex items-center gap-2 text-brand-red bg-brand-mist rounded-xl px-5 py-3 text-sm" data-testid="selected-session-banner">
                <MapPin className="w-4 h-4" /> {selectedSession.park_name} · {selectedSession.date} at {selectedSession.time}
              </div>
            )}
            {packBalance > 0 && (
              <div className="mt-4 flex items-center gap-2 text-green-700 bg-green-50 rounded-xl px-5 py-3 text-sm" data-testid="pack-balance-banner">
                <CheckCircle2 className="w-4 h-4" /> Welcome back! You have <strong>&nbsp;{packBalance}&nbsp;</strong> session{packBalance > 1 ? "s" : ""} left in your pack.
              </div>
            )}
            <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="booking-form">
              <Field label="Your name" value={form.name} onChange={set("name")} required testid="booking-name" />
              <Field label="Email" type="email" value={form.email} onChange={set("email")} required testid="booking-email" />
              <Field label="Phone" value={form.phone} onChange={set("phone")} testid="booking-phone" />
              <Field label="Pet's name" value={form.pet_name} onChange={set("pet_name")} testid="booking-pet" />
              <Field label="Preferred date" type="date" value={form.date} onChange={set("date")} required testid="booking-date" />
              <Field label="Preferred time" type="time" value={form.time} onChange={set("time")} required testid="booking-time" />
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">Notes</label>
                <textarea value={form.notes} onChange={set("notes")} rows={3} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid="booking-notes" />
              </div>
              {packBalance > 0 && (
                <label className="sm:col-span-2 flex items-center gap-3 bg-brand-mist rounded-xl px-4 py-3 cursor-pointer" data-testid="use-pack-toggle">
                  <input type="checkbox" checked={usePack} onChange={(e) => setUsePack(e.target.checked)} className="w-4 h-4 accent-brand-red" data-testid="use-pack-checkbox" />
                  <span className="text-sm">Use a session from your pack — <strong>{packBalance} left</strong></span>
                </label>
              )}
              <button type="submit" disabled={submitting} className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors disabled:opacity-60" data-testid="booking-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Send booking request <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">{label}{required && <span className="text-brand-red"> *</span>}</label>
      <input type={type} value={value} onChange={onChange} required={required} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={testid} />
    </div>
  );
}
